import React from 'react';
import { Dropdown, Form, Header, Segment } from 'semantic-ui-react';
import {
    DIFFERENT_VARIABLES_BRIDGE_NAME, OUTGOING_BRIDGE_KIND, OUTGOING_BRIDGE_LABELS, SAME_VARIABLE_BRIDGE_NAME,
} from '../../../../../../../../../constnats/outgoingBridge';
import { outgoingBridgesSchema } from '../../../../../../../../../schemas/outgoingBridge.schemas';
import type { OutgoingBridgeType, OutgoingBridgesType } from '../../../../../../../../../schemas/outgoingBridge.schemas';
import type { DescriptorType } from '../../../../../../../../../types/descriptor.types';
import {
    getBridgeEndDescriptorOptions, getOutgoingBridges, getSameVariableBridgeName, updateBridges,
} from '../../../../../../../../../utils/descriptor/outgoingBridges';
import type { BridgeEndDescriptorOptionType } from '../../../../../../../../../utils/descriptor/outgoingBridges';
import './outgoingBridgesField.css';

const bridgeNameOptions = Object.values(SAME_VARIABLE_BRIDGE_NAME).map((name) => ({ key: name, value: name, text: name }));

type OutgoingBridgesFieldPropsType = {
    value?: OutgoingBridgesType | null;
    onChange: (value: OutgoingBridgesType) => void;
    availableDescriptors: DescriptorType[];
    disabled?: boolean;
};

function OutgoingBridgesField({
    value, onChange, availableDescriptors, disabled = false,
}: OutgoingBridgesFieldPropsType) {
    const outgoingBridges = getOutgoingBridges(value);
    const sameVariableBridges = outgoingBridges.sameVariable;
    const differentVariableBridges = outgoingBridges.differentVariables;
    const sameVariableBridgeName = getSameVariableBridgeName(sameVariableBridges);
    const validation = outgoingBridgesSchema.safeParse(outgoingBridges);
    const collectionErrors = validation.success ? undefined : validation.error.flatten().fieldErrors;

    const sameVariableBridgeEndOptions = getBridgeEndDescriptorOptions(
        availableDescriptors, sameVariableBridges, differentVariableBridges.map((bridge) => bridge.endId),
    );
    const differentVariableBridgeEndOptions = getBridgeEndDescriptorOptions(
        availableDescriptors, differentVariableBridges, sameVariableBridges.map((bridge) => bridge.endId),
    );

    return (
        <Segment>
            <Header as="h5" textAlign="center">Outgoing Bridges</Header>
            <VariableCollectionBridgesField
                id="outgoing-bridges-same-variable"
                label={OUTGOING_BRIDGE_LABELS[OUTGOING_BRIDGE_KIND.SAME_VARIABLE]}
                existingBridges={sameVariableBridges}
                bridgeName={sameVariableBridgeName}
                bridgeEndDescriptorOptions={sameVariableBridgeEndOptions}
                onChange={(updatedBridges) => onChange({ ...outgoingBridges, sameVariable: updatedBridges })}
                disabled={disabled} error={Boolean(collectionErrors?.sameVariable?.length)}
            />
            {sameVariableBridges.length > 0 && (
                <Form.Field error={!sameVariableBridgeName}>
                    <label htmlFor="outgoing-bridges-same-variable-name">Bridge name</label>
                    <Dropdown
                        id="outgoing-bridges-same-variable-name" aria-label="Bridge name"
                        placeholder="Select a bridge name" fluid selection disabled={disabled}
                        options={bridgeNameOptions} value={sameVariableBridgeName}
                        onChange={(_, data) => {
                            const name = data.value;
                            if (typeof name === 'string') {
                                onChange({
                                    ...outgoingBridges,
                                    sameVariable: sameVariableBridges.map((bridge) => ({ ...bridge, name })),
                                });
                            }
                        }}
                    />
                    {!sameVariableBridgeName && <span role="status">Select a bridge name.</span>}
                </Form.Field>
            )}
            <VariableCollectionBridgesField
                id="outgoing-bridges-different-variables"
                label={OUTGOING_BRIDGE_LABELS[OUTGOING_BRIDGE_KIND.DIFFERENT_VARIABLES]}
                existingBridges={differentVariableBridges}
                bridgeName={DIFFERENT_VARIABLES_BRIDGE_NAME}
                bridgeEndDescriptorOptions={differentVariableBridgeEndOptions}
                onChange={(updatedBridges) => onChange({ ...outgoingBridges, differentVariables: updatedBridges })}
                disabled={disabled} error={Boolean(collectionErrors?.differentVariables?.length)}
            />
        </Segment>
    );
}

type VariableCollectionBridgesFieldPropsType = {
    id: string;
    label: string;
    existingBridges: OutgoingBridgeType[];
    bridgeName: string;
    bridgeEndDescriptorOptions: BridgeEndDescriptorOptionType[];
    onChange: (bridges: OutgoingBridgeType[]) => void;
    disabled: boolean;
    error: boolean;
};

function VariableCollectionBridgesField({
    id, label, existingBridges, bridgeName, bridgeEndDescriptorOptions, onChange, disabled, error,
}: VariableCollectionBridgesFieldPropsType) {
    const bridgeEndDescriptorIds = existingBridges.map((bridge) => bridge.endId);
    const hasInvalidEndDescriptors = bridgeEndDescriptorOptions.some((option) => option.invalid);
    const dropdownOptions = bridgeEndDescriptorOptions.map((option) => ({
        key: option.descriptorId,
        value: option.descriptorId,
        text: `${option.invalid ? '(INVALID) ' : ''}${option.descriptorLabel}`,
        className: option.invalid ? 'outgoingBridges__descriptor--invalid' : undefined,
        disabled: option.invalid,
    }));

    return (
        <div>
            <Form.Field error={error || hasInvalidEndDescriptors}>
                <label htmlFor={id}>{label}</label>
                <Dropdown
                    id={id} aria-label={label}
                    className="outgoingBridges__ends" placeholder="Select bridge ends"
                    fluid multiple search selection disabled={disabled} options={dropdownOptions}
                    value={bridgeEndDescriptorIds}
                    renderLabel={(item) => ({ content: item.text, className: item.className })}
                    onChange={(_, data) => {
                        const selectedDescriptorIds = Array.isArray(data.value)
                            ? data.value.filter((descriptorId): descriptorId is string => typeof descriptorId === 'string') : [];
                        onChange(updateBridges(existingBridges, selectedDescriptorIds, bridgeEndDescriptorOptions, bridgeName));
                    }}
                />
            </Form.Field>
        </div>
    );
}

export default OutgoingBridgesField;
